import { SxProps, Theme } from "@mui/material";
import { ReactNode } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
    DateTimePicker,
    DateTimePickerProps,
} from "@mui/x-date-pickers/DateTimePicker";
import { Dayjs } from "dayjs";
import { ApplicationTooltip } from "../../../screens/positions-and-orders/views";

type ApplicationDateTimePickerTooltipPlacement =
    'right-start' | 'left-start' | 'bottom-start' | 'bottom-end' | 'top-start';

type ApplicationDateTimePickerProps = Omit<DateTimePickerProps<Dayjs>, "ampm" | "format" | "slotProps" | "sx"> & {
    width?: number | string;
    format?: string;
    slotProps?: DateTimePickerProps<Dayjs>["slotProps"];
    sx?: SxProps<Theme>;
    tooltipTitle?: ReactNode;
    tooltipPlacement?: ApplicationDateTimePickerTooltipPlacement;
    tooltipMaxWidth?: number | string;
};

const desktopPaperScrollbarSx = {
    "& *::-webkit-scrollbar": {
        width: "6px",
        height: "6px",
    },
    "& *::-webkit-scrollbar-thumb": {
        backgroundColor: "#999",
        borderRadius: "8px",
    },
    "& *::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
    },
    "& *": {
        scrollbarWidth: "thin",
    },
    "& .MuiMultiSectionDigitalClockSection-root": {
        pr: "6px",
        boxSizing: "content-box",
        scrollbarGutter: "stable",
    },
    "& .MuiMultiSectionDigitalClockSection-root::-webkit-scrollbar": {
        width: "8px",
    },
};

export function ApplicationDateTimePicker({
    width = 210,
    format = "ddd DD.MMM.YYYY HH:mm",
    slotProps,
    sx,
    tooltipTitle,
    tooltipPlacement,
    tooltipMaxWidth,
    ...dateTimePickerProps
}: ApplicationDateTimePickerProps) {
    const textFieldSlotProps = slotProps?.textField as any;
    const desktopPaperSlotProps = slotProps?.desktopPaper as any;
    const picker = (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
                {...dateTimePickerProps}
                slotProps={{
                    ...slotProps,
                    textField: {
                        ...textFieldSlotProps,
                        size: textFieldSlotProps?.size ?? "small",
                        variant: textFieldSlotProps?.variant ?? "standard",
                        InputProps: {
                            ...textFieldSlotProps?.InputProps,
                            sx: textFieldSlotProps?.InputProps?.sx,
                        },
                    },
                    desktopPaper: {
                        ...desktopPaperSlotProps,
                        sx: {
                            ...desktopPaperScrollbarSx,
                            ...desktopPaperSlotProps?.sx,
                        },
                    },
                }}
                sx={{
                    width,
                    ...(sx as any),
                }}
                format={format}
                ampm={false}
            />
        </LocalizationProvider>
    );

    return (
        <ApplicationTooltip
            title={tooltipTitle}
            placement={tooltipPlacement}
            maxWidth={tooltipMaxWidth}
            offset={[0, -7]}
            disableInteractive={true}
        >
            <span>{picker}</span>
        </ApplicationTooltip>
    );
}
